// Replaces the `recaptcha-v3` package (removed, unmaintained for Vue 3) with a direct integration
// against Google's reCAPTCHA v3 script, matching its `load(siteKey, { autoHideBadge: true })` +
// `.execute('submit')` behavior. The badge is hidden (not just via `autoHideBadge`'s own CSS, which
// only ships with the removed package) because callers pair this with a visible
// `MoleculeRecaptchaDisclaimer`, per Google's reCAPTCHA ToS requirement to show one or the other.
//
// `loadRecaptcha`'s cache lives in a closure (via this factory), not a bare module-level `let`: this
// module is a singleton for the life of the Node server process, shared across every SSR request it
// handles, so a top-level mutable binding is the wrong shape for anything meant to hold request- or
// session-specific state. In practice `getRecaptchaToken` is only ever called from client-triggered form
// submit handlers (see the three importers), never during SSR render, so nothing is actually leaking
// across requests today - but nothing structural enforced that invariant either, and the closure form
// costs nothing while making it explicit.
export function useRecaptchaLoader() {
  let recaptchaReadyPromise: Promise<void> | undefined

  return function loadRecaptcha(siteKey: string) {
    if (recaptchaReadyPromise) {
      return recaptchaReadyPromise
    }

    recaptchaReadyPromise = new Promise((resolve) => {
      const style = document.createElement('style')
      style.textContent = '.grecaptcha-badge { visibility: hidden; }'
      document.head.appendChild(style)

      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
      script.onload = () => (window as any).grecaptcha.ready(resolve)
      document.head.appendChild(script)
    })

    return recaptchaReadyPromise
  }
}

// One shared loader for the module's lifetime, so the script/style tags are only ever injected once per
// browser session even when multiple forms on the same page call getRecaptchaToken.
const loadRecaptcha = useRecaptchaLoader()

export async function getRecaptchaToken(siteKey: string) {
  await loadRecaptcha(siteKey)
  return await (window as any).grecaptcha.execute(siteKey, { action: 'submit' })
}
